<script setup lang="ts">
import type {
  ISectionFormExpose,
  TSectionCategoryFormOption,
  TSectionFormSubmitValues,
  TSiteSectionFormValues,
} from "@/app/tools/new-section-management/section-form/section-form.types";
import type { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import type { TSiteDetails } from "@/data/api/kili/sites/types/sites.type";
import type { SiteSectionCategoryId } from "@/data/common/ids";
import type { SectionNode } from "@/domains/v4b/site-section/site-section.types";
import type { TranslationKey } from "@/locales/typed-intl";

import { toTypedSchema } from "@vee-validate/zod";
import { TextSelect, type IVirtualizedTreeNode } from "@voltalis/ui-kit/components";
import { PlusIcon } from "@voltalis/ui-kit/icons";
import { useForm } from "vee-validate";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";

import { siteSectionFormSchema } from "@/app/tools/new-section-management/section-form/section-form.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import Select from "primevue/select";

import SelectSectionParentDialog from "@/app/tools/new-section-management/site-section-creation/select-section-parent-dialog.vue";
import CategoryCreationForm from "@/components/tools/site-section/category-creation/category-creation-form.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  isLoading?: boolean;
  siteDetails?: TSiteDetails;
  initialSelectedParent?: TSiteSectionsTreeData;
  maybeInitialSectionParent?: TSiteSectionsTreeData;
  siteSectionsTree?: IVirtualizedTreeNode<TSiteSectionsTreeData>;
  initialSection?: TSiteSectionsTreeData;
  disabledFields?: Array<keyof TSiteSectionFormValues>;
  targetSiteRootTranslationKey?: TranslationKey;
  isParentSelectionNodeDisabled?: (node: IVirtualizedTreeNode<TSiteSectionsTreeData>) => boolean;
  categoryOptions?: readonly TSectionCategoryFormOption[];
  createCategory?: (name: string) => Promise<{ id: SiteSectionCategoryId } | undefined>;
  isCreatingCategory?: boolean;
}

interface IEmits {
  (e: "submit"): void;
  (e: "select-parent", newParent: TSiteSectionsTreeData): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();
const dirty = defineModel<boolean>("dirty", {
  required: false,
  default: false,
});

const { t, k, rawT } = useTypedIntl();

const isSelectSectionParentDialog = ref<boolean>(false);
const isCategoryCreationFormVisible = ref<boolean>(false);
const createdCategory = ref<TSectionCategoryFormOption | null>(null);
const nameInput = useTemplateRef<{ $el?: HTMLInputElement; focus?: () => void }>("nameInput");
const categoryCreationForm = useTemplateRef<{ focusInput: () => Promise<void> }>("categoryCreationForm");

const initialValues: TSiteSectionFormValues = {
  name: props.initialSection?.name ?? "",
  area: props.initialSection?.area ?? null,
  parent: props.initialSelectedParent ?? null,
  categoryId: props.initialSection?.category?.id ?? null,
};

const { errors, handleSubmit, resetForm, defineField, setFieldError, meta } = useForm({
  validationSchema: toTypedSchema(siteSectionFormSchema),
  initialValues,
});

const [name] = defineField("name");
const [area] = defineField("area");
const [parent] = defineField("parent");
const [categoryId] = defineField("categoryId");

const selectedParentText = computed<string>(() => {
  const parentName = parent.value?.name;

  if (!parentName)
    return t(k.tools.siteManagement.siteTreeStructure.creation.form.root({ siteName: props.siteDetails?.name ?? "-" }));

  return parentName;
});
const categoryOptions = computed<TSectionCategoryFormOption[]>(() => {
  const options = [...(props.categoryOptions ?? [])];

  const initialCategory = props.initialSection?.category
    ? {
        id: props.initialSection.category.id,
        label: props.initialSection.category.name,
      }
    : null;

  for (const category of [initialCategory, createdCategory.value]) {
    if (category && !options.some((option) => option.id === category.id)) {
      options.push(category);
    }
  }

  return options;
});

const categorySelectOptions = computed<Array<TSectionCategoryFormOption | { id: null; label: string }>>(() => {
  return [
    {
      id: null,
      label: t(k.tools.siteManagement.siteTreeStructure.creation.form.category.none),
    },
    ...categoryOptions.value,
  ];
});

function handleSelectParentSection(selectedSection: TSiteSectionsTreeData) {
  parent.value = selectedSection;
  emits("select-parent", selectedSection);
  isSelectSectionParentDialog.value = false;
}

function isFieldDisabled(key: keyof TSiteSectionFormValues): boolean {
  return props.disabledFields?.includes(key) ?? false;
}

async function focusNameInput(): Promise<void> {
  await nextTick();

  const input = nameInput.value;
  const element = input?.$el ?? input;

  element?.focus?.();
}

async function startCategoryCreation() {
  isCategoryCreationFormVisible.value = true;

  await nextTick();

  await categoryCreationForm.value?.focusInput();
}

function cancelCategoryCreation() {
  isCategoryCreationFormVisible.value = false;
}

function resetSiteSectionForm() {
  resetForm();
  createdCategory.value = null;
  cancelCategoryCreation();
}

async function confirmCategoryCreation(name: string) {
  if (!props.createCategory) return;

  const maybeCreatedCategory = await props.createCategory(name);

  if (maybeCreatedCategory) {
    createdCategory.value = {
      id: maybeCreatedCategory.id,
      label: name,
    };
    categoryId.value = maybeCreatedCategory.id;
    cancelCategoryCreation();
  }
}

const submit = handleSubmit((values) => {
  const submitValues: TSectionFormSubmitValues = {
    newValues: values,
    initialValues,
  };

  return submitValues;
});

defineExpose<ISectionFormExpose>({
  focusNameInput,
  submit,
  resetForm: resetSiteSectionForm,
  setSiteSectionsFieldsetCustomError: setFieldError,
});

watch(
  () => props.initialSelectedParent,
  (newValue) => {
    parent.value = newValue ?? null;
  },
  { immediate: true },
);

watch(meta, (newValue) => {
  dirty.value = newValue.dirty;
});
</script>

<template>
  <form
    class="flex flex-col gap-6 w-full"
    @submit="emits('submit')"
  >
    <section class="flex flex-col gap-2">
      <label
        id="name-label"
        for="name"
        class="font-semibold text-text-secondary text-nowrap"
        >{{ t(k.tools.siteManagement.siteTreeStructure.creation.form.name.label) }}</label
      >
      <InputText
        ref="nameInput"
        v-model.trim="name"
        autofocus
        aria-labelledby="name-label"
        :data-test-id="DATA_TEST_IDS.components.createSiteSectionDrawer.createForm.nameTextInput"
        :disabled="props.isLoading || isFieldDisabled('name')"
        :invalid="!!errors.name"
        id="name"
        :placeholder="t(k.tools.siteManagement.siteTreeStructure.creation.form.name.placeholder)"
        required
        @keydown.enter="emits('submit')"
      />
      <small
        v-if="errors.name"
        class="text-red-500"
      >
        {{ rawT(`veeValidate.messages.${errors.name}`) }}
      </small>
    </section>

    <section class="flex flex-col gap-2">
      <label
        id="area-label"
        for="area"
        class="font-semibold text-text-secondary text-nowrap"
        >{{ t(k.tools.siteManagement.siteTreeStructure.creation.form.area.label) }}
        <span class="italic">({{ t(k.global.misc.optional) }})</span>
      </label>
      <InputNumber
        v-model="area"
        aria-labelledby="area-label"
        :data-test-id="DATA_TEST_IDS.components.createSiteSectionDrawer.createForm.areaNumberInput"
        :disabled="props.isLoading || isFieldDisabled('area')"
        :invalid="!!errors.area"
        id="area"
        :placeholder="t(k.tools.siteManagement.siteTreeStructure.creation.form.area.placeholder)"
        :required="false"
        suffix=" m2"
        @keydown.enter="emits('submit')"
      />
      <small
        v-if="errors.area"
        class="text-red-500"
      >
        {{ rawT(`veeValidate.messages.${errors.area}`, { min: 0 }) }}
      </small>
    </section>

    <section class="flex flex-col gap-2">
      <label
        id="parent-label"
        class="font-semibold text-text-secondary text-nowrap"
        >{{ t(k.tools.siteManagement.siteTreeStructure.creation.form.parentSection) }}</label
      >
      <TextSelect
        :content="selectedParentText"
        :change-text="t(k.global.misc.change)"
        :data-test-id="DATA_TEST_IDS.components.createSiteSectionDrawer.createForm.selectParentTextSelect"
        :disabled="props.isLoading || isFieldDisabled('parent')"
        @change="isSelectSectionParentDialog = true"
      />
    </section>

    <section
      v-if="props.createCategory || props.categoryOptions"
      class="flex flex-col gap-2"
    >
      <label
        id="category-label"
        class="font-semibold text-text-secondary text-nowrap"
      >
        {{ t(k.tools.siteManagement.siteTreeStructure.creation.form.category.label) }}
        <span class="italic">({{ t(k.global.misc.optional) }})</span>
      </label>

      <div class="flex flex-col gap-2">
        <Select
          v-model="categoryId"
          option-label="label"
          option-value="id"
          data-key="id"
          reset-filter-on-hide
          :options="categorySelectOptions"
          :placeholder="t(k.tools.siteManagement.siteTreeStructure.creation.form.category.none)"
          :disabled="props.isLoading"
          :data-test-id="DATA_TEST_IDS.components.createSiteSectionDrawer.createForm.categorySelect"
        />

        <CategoryCreationForm
          v-if="props.createCategory && isCategoryCreationFormVisible"
          ref="categoryCreationForm"
          :is-loading="props.isCreatingCategory"
          @confirm="confirmCategoryCreation"
          @cancel="cancelCategoryCreation"
        />

        <HospitalityButton
          v-else-if="props.createCategory"
          :data-test-id="DATA_TEST_IDS.components.createSiteSectionDrawer.createForm.categoryCreateButton"
          :disabled="props.isLoading"
          :icon="PlusIcon"
          :label="t(k.tools.siteManagement.siteTreeStructure.createCategory)"
          severity="link"
          size="sm"
          @click="startCategoryCreation"
        />
      </div>
    </section>
  </form>

  <SelectSectionParentDialog
    v-model="isSelectSectionParentDialog"
    :initialSelectedParent="(parent as SectionNode) ?? null"
    :is-node-disabled="props.isParentSelectionNodeDisabled"
    :maybe-initial-section-parent="props.maybeInitialSectionParent"
    :pending-preview-name="
      props.initialSection?.name ?? t(k.tools.siteManagement.siteTreeStructure.creation.pendingPreviewName)
    "
    :pending-preview-section="props.initialSection ?? undefined"
    :siteSectionsTree="props.siteSectionsTree"
    :target-site-root-translation-key="props.targetSiteRootTranslationKey"
    @select="handleSelectParentSection($event)"
  />
</template>
